<!--
    Params
      - node - node to edit
-->

<template>
  <ValidationObserver
    ref="flowNodeObserver"
    v-slot="{ valid }" >
    <b-modal
      id="questions-modal"
      ref="flowNodeModal"
      modal-class="node-modal"
      :body-class="['dark-background']"
      :size="size"
      centered
      @hidden="reset"
    >
      <template slot="modal-header">
        <Vnodes
          :vnodes="title"/>
      </template>

      <JsonSchema
        ref="jsonSchema"
        v-model="formValue"
        :schema="schema | processSchema"
        :components="componentsConfig"
        :ui-schema="uiSchema"
        :wrapper="wrapperComponentConfig"
        @init-default="initDefault"
        @input="() => {}" />

      <template slot="modal-footer">
        <div class="modal-footer__buttons flex flex-row flex-nowrap">
          <b-button
            :disabled="working"
            @click="hide" >{{ cancelButtonText }}</b-button>
          <b-button
            class=""
            :disabled="!schemaValid || working"
            :variant="buttonVariant"
            :class="{loading: working}"
            @click="confirm" >
            <span>{{ confirmButtonText }}</span>
            <Spinner
              v-if="working"
              class="absolute"
              :size="14"
              :thickness="2"
              :color="SBColors.white" />
          </b-button>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapGetters } from 'vuex';
import JsonSchema from '@roma219/vue-jsonschema-form';

import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import Spinner from '@/components/common/Spinner';
import ToasterMixin from '@/mixins/Toaster';
import FormSchemaMixin from '../../FormSchema.mixin';

export default {
  name: 'QuestionModal',
  components: {
    Spinner,
    JsonSchema,
    Vnodes: {
      functional: true,
      render: (h, ctx) => ctx.props.vnodes,
    },
  },
  mixins: [
    ToasterMixin,
    FormSchemaMixin,
  ],
  data() {
    return {
      formValue: {},
      editMode: false,
      editIndex: null,
      confirmButtonText: 'Save',
      variant: 'info',
      size: '',
      buttonVariant: 'primary',
      cancelButtonText: 'Cancel',
      working: false,
      node: null,
      SBIconList,
      SBColors,
      schemaBP: {
        type: 'object',
        properties: {
          replyContains: {
            type: 'input',
            value: '',
            title: 'If a contact reply contains',
            placeholder: 'A request for scheduling a call',
            validators: 'required',
          },
          title: {
            type: 'group-title',
            title: 'Then ask the following questions',
          },
          questions: {
            type: 'questions',
            value: [],
            addQuestionIcon: SBIconList.PLUS,
            addQuestionText: 'Add question',
            minQuestions: 1,
            maxQuestions: 10,
            placeholder: 'What is your full name and address?',
            // enter: 'Add',
            child: true,
            noTopMargin: true,
            returnKey: 'questionsList',
            validators: 'required|maxLength:1000',
            validatorMessages: {
              required: 'Question text is required.',          // Set this to prevent unwanted default messages
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      flowsDefinitionsUI: 'flows/flowsDefinitionsUI', // TODO delete
      updatingFlow: 'flows/updatingFlow',
    }),
    title() {
      const h = this.$createElement;
      const title = h(
        'h2',
        { class: 'flex center-all' },
        [
          this.editMode ? 'Add questions' : 'Add questions',
        ],
      );
      return title;
    },
  },
  beforeMount() {
    this.$eventBus.$on('showFlowNodeModal', (params) => {
      if (!this.updatingFlow) {
        this.show(params);
      } else {
        this.showToaster('Wait for the flow to update before editing a block.', null, null, 'warning');
      }
    });
  },
  methods: {
    focusFirstInput() {
      const input = document.getElementById('questions-modal').querySelector('input.empty, .ql-editor.ql-blank');
      if (input) input.focus();
    },
    show(params, index) {
      this.working = false;
      if (!this.emptyObject(params)) {
        this.editMode = true;
        this.editIndex = index;
        // this.node = params.node;

        this.schemaBP.properties.replyContains.value = params.label;
        this.schemaBP.properties.questions.value = params.questions.map(q => ({ text: q.text, buttons: { value: q.buttons } }));
      } else {
        this.editMode = false;
        this.schemaBP.properties.replyContains.value = '';
        this.schemaBP.properties.questions.value = [];
        this.editIndex = null;
      }

      this.$refs.flowNodeModal.show();
      setTimeout(() => {
        this.focusFirstInput();
      }, 0);
    },
    hide() {
      this.$refs.flowNodeModal.hide();
    },
    /** Setting the working to true triggers the submit action in the specific component. */
    confirm() {
      this.$refs.flowNodeObserver.validate()
        .then((valid) => {
          // if (valid) {
          //   this.working = true;
          // }
          this.$emit('save', { value: this.formValue, editMode: this.editMode, editIndex: this.editIndex });
          this.hide();
          this.reset();
        });
    },
    submitted(data) {
      this.hide();
    },
    error(error) {
      this.working = false;
      this.showToaster('There was an error. Try again later.', null, null, 'error');
    },
    reset() {
      this.size = '';
      this.working = false;
      this.$refs.flowNodeObserver.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.node-modal {
  .modal-content {
    .modal-header {
      padding: 20px;
      box-shadow: none;
      border-bottom: solid 1px $greyBorderModal;
      display: flex;
      flex-direction: column;

      h2 {
        font-size: 16px;
        font-weight: 500 !important;
        color: $blackPrimary;
        margin-bottom: 0px;
        gap: 10px;
      }

      p {
        font-size: 14px;
        color: $blackUltralight;
      }
    }
    .modal-body {
      padding: 20px;
      font-size: 14px;
      font-weight: normal;
      line-height: 1.2;
      color: $blackPrimary;
      background-color: $greyUltralight;
    }
    .modal-footer {
      border-top: solid 1px $greyBorderModal;
      align-items: center;
      flex-direction: row;
    }
    .modal-footer>* {
      margin: 0;
    }
    .modal-footer__message {
      // style if needed
    }
    .modal-footer__buttons {
      // style if needed
      .btn.loading {
        position: relative;

        span {
          visibility: hidden;
        }

        .loader {
          position: absolute;
          height: 100%;
          display: flex;
          align-items: center;
        }
      }
    }
    .modal-footer__undo-warning {
      color: $redPrimary;
      font-size: 14px;
      font-weight: 400;
      // icon 4px from text
      .sb2-icon {
        margin-right: 4px;
      }
    }
  }
}

.subtitle {
  margin-top: 3px !important;
  font-size: 12px !important;
  font-weight: normal !important;
  text-align: left !important;
  color: $blackUltralight !important;
}

.modal {
  .modal-content {
    border-radius: 10px !important;
  }

  .modal-header {
    padding: 18px 15px !important;
    h3 {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.2;
    }
    p {
      font-size: 12px;
      font-weight: 400;
      color: $blackUltralight;
    }
  }

  .modal-body {
    // padding: 18px 15px !important;
    padding: 15px !important;
    // min-height: 212px;

    // &.no-bottom-padding {
    //   padding-bottom: 0 !important;
    // }

    .add-button {
      position: sticky;
      bottom: 0px;
      background: $greyUltralight;
      // margin: 0 -15px;
      padding: 18px 15px;

      * {
        font-size: 14px;
        font-weight: 500;
        color: $blackPrimary;
      }

      > div {
        min-height: 34px;
        padding: 9px 12px;
        border-radius: 10px;
        background: $greyBorderSecondary;
        // cursor: pointer;
        &:hover {
          background-color: #DDE6E9;
        }
      }
    }
  }

  .modal-footer {
    button {
      border-radius: 10px;

      &.btn-secondary {
        border: solid 1px $greyBorder;
      }
    }
  }
}
</style>
